/* Casanvero — Nocturne concept. One small script, no dependencies.
   Every effect is progressive: markup is complete and readable without it. */
(() => {
  const doc = document;
  const root = doc.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = window.matchMedia('(pointer: fine)').matches;
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  root.classList.add('cv-js');

  /* ---------- load: start the hero zoom + headline once the page can paint ---------- */
  const markLoaded = () => requestAnimationFrame(() => {
    root.classList.add('cv-loaded');
    doc.querySelectorAll('[data-split-now]').forEach((el) => el.classList.add('is-in'));
  });
  if (doc.readyState === 'complete') markLoaded(); else window.addEventListener('load', markLoaded);
  setTimeout(markLoaded, 1200); // never let a slow asset hold the headline back

  /* ---------- reveal on scroll ---------- */
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      e.target.classList.add('is-in');
      io.unobserve(e.target);
      if (e.target.matches('[data-count-wrap]')) countIn(e.target);
    });
  }, { threshold: 0.2, rootMargin: '0px 0px -6% 0px' });
  doc.querySelectorAll('[data-reveal], [data-split]:not([data-split-now]), [data-count-wrap]').forEach((el) => io.observe(el));

  /* ---------- count-up, keeps the source's decimal places ---------- */
  function countIn(scope) {
    scope.querySelectorAll('[data-count]').forEach((el) => {
      const raw = el.getAttribute('data-count');
      const target = parseFloat(raw);
      if (isNaN(target) || reduce) return;
      const dec = (raw.split('.')[1] || '').length;
      let t0 = null;
      const tick = (now) => {
        if (t0 === null) t0 = now;
        const p = Math.min((now - t0) / 1600, 1);
        el.textContent = ((p === 1 ? 1 : 1 - Math.pow(2, -10 * p)) * target).toFixed(dec);
        if (p < 1) requestAnimationFrame(tick);
      };
      el.textContent = (0).toFixed(dec);
      requestAnimationFrame(tick);
    });
  }

  /* ---------- header: solid after the hero starts to move, hides while reading down ---------- */
  const header = doc.querySelector('[data-cv-header]');
  const menuBtn = doc.querySelector('[data-cv-menu-toggle]');
  let lastY = window.scrollY;
  const setMenu = (open) => {
    root.classList.toggle('cv-menu-open', open);
    if (menuBtn) menuBtn.setAttribute('aria-expanded', String(open));
    doc.body.style.overflow = open ? 'hidden' : '';
  };
  if (menuBtn) menuBtn.addEventListener('click', () => setMenu(!root.classList.contains('cv-menu-open')));
  doc.addEventListener('keydown', (e) => { if (e.key === 'Escape') setMenu(false); });
  doc.querySelectorAll('.cv-menu a').forEach((a) => a.addEventListener('click', () => setMenu(false)));

  /* ---------- "right now" pill: what the home would be doing at the visitor's local time ---------- */
  const nowEl = doc.querySelector('[data-cv-now]');
  const schedule = [...doc.querySelectorAll('[data-moment]')].map((m) => ({
    mins: +m.getAttribute('data-mins'), title: m.getAttribute('data-now') || m.getAttribute('data-title'),
  })).sort((a, b) => a.mins - b.mins);
  const fmt = (mins) => {
    const h = Math.floor(mins / 60) % 24, m = Math.floor(mins % 60);
    return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
  };
  function updateNow() {
    if (!nowEl || !schedule.length) return;
    const d = new Date();
    const mins = d.getHours() * 60 + d.getMinutes();
    let cur = schedule[schedule.length - 1];
    for (const s of schedule) if (s.mins <= mins) cur = s;
    nowEl.querySelector('[data-now-time]').textContent = fmt(mins);
    nowEl.querySelector('[data-now-text]').textContent = cur.title;
  }
  updateNow();
  setInterval(updateNow, 30000);

  /* ---------- gallery: drag, arrows, progress ---------- */
  doc.querySelectorAll('[data-cv-gallery]').forEach((gal) => {
    const track = gal.querySelector('[data-track]');
    const bar = gal.querySelector('[data-progress]');
    const cur = gal.querySelector('[data-current]');
    const prev = gal.querySelector('[data-prev]');
    const next = gal.querySelector('[data-next]');
    const items = [...track.children];
    const step = () => (items[1] ? items[1].offsetLeft - items[0].offsetLeft : track.clientWidth);
    const sync = () => {
      const max = track.scrollWidth - track.clientWidth;
      const p = max > 0 ? track.scrollLeft / max : 1;
      if (bar) bar.style.setProperty('--p', Math.max(0.12, p).toFixed(3));
      const idx = Math.min(items.length, Math.round(track.scrollLeft / step()) + 1);
      if (cur) cur.textContent = String(idx).padStart(2, '0');
      if (prev) prev.disabled = track.scrollLeft < 4;
      if (next) next.disabled = track.scrollLeft > max - 4;
    };
    track.addEventListener('scroll', () => requestAnimationFrame(sync), { passive: true });
    prev && prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next && next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    sync();
    if (!fine) return;
    // mouse drag-to-scroll; touch already scrolls natively
    let down = false, startX = 0, startL = 0, moved = false;
    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse') return;
      down = true; moved = false; startX = e.clientX; startL = track.scrollLeft;
    });
    window.addEventListener('pointermove', (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 4) { moved = true; track.classList.add('is-dragging'); }
      track.scrollLeft = startL - dx;
    });
    window.addEventListener('pointerup', () => {
      if (!down) return;
      down = false;
      track.classList.remove('is-dragging');
      if (moved) {
        const s = step();
        track.scrollTo({ left: Math.round(track.scrollLeft / s) * s, behavior: 'smooth' });
      }
    });
  });

  /* ---------- the quiet hours ---------- */
  const hours = doc.querySelector('[data-cv-hours]');
  let hoursUpdate = null;
  if (hours) {
    const startM = +hours.getAttribute('data-start');
    const endM = +hours.getAttribute('data-end');
    const hand = hours.querySelector('[data-hand]');
    const sweep = hours.querySelector('[data-sweep]');
    const timeEl = hours.querySelector('[data-dial-time]');
    const labelEl = hours.querySelector('[data-dial-label]');
    const moments = [...hours.querySelectorAll('[data-moment]')];
    const events = [...hours.querySelectorAll('[data-ev]')].map((el) => ({ el, mins: +el.getAttribute('data-ev'), span: +(el.getAttribute('data-span') || 0) }));
    const circ = sweep ? +sweep.getAttribute('data-circ') : 0;
    let lastActive = -1;
    hoursUpdate = () => {
      const r = hours.getBoundingClientRect();
      const run = hours.offsetHeight - window.innerHeight;
      const p = clamp(-r.top / (run || 1), 0, 1);
      const mins = startM + p * (endM - startM);
      if (hand) hand.style.setProperty('--rot', (mins * 0.25).toFixed(2) + 'deg');
      if (sweep) sweep.setAttribute('stroke-dasharray', `${(circ * (mins - startM) / 1440).toFixed(1)} ${circ}`);
      if (timeEl) timeEl.textContent = fmt(mins);
      // daylight: 0 at night, peaking at 1 around 13:00
      const sun = clamp(Math.sin(((mins - 360) / (1200 - 360)) * Math.PI), 0, 1);
      hours.style.setProperty('--sun', sun.toFixed(3));
      let active = 0;
      moments.forEach((m, i) => { if (+m.getAttribute('data-mins') <= mins + 1) active = i; });
      if (active !== lastActive) {
        moments.forEach((m, i) => m.classList.toggle('is-active', i === active));
        if (labelEl) labelEl.textContent = moments[active].getAttribute('data-title');
        lastActive = active;
      }
      events.forEach((ev) => ev.el.classList.toggle('is-hot', mins >= ev.mins - 12 && mins <= ev.mins + Math.max(ev.span, 0) + 28));
    };
  }

  /* ---------- manifesto: words light up as the paragraph crosses the viewport ---------- */
  const words = doc.querySelector('[data-words]');
  let wordsUpdate = null;
  if (words) {
    const text = words.textContent.trim().split(/\s+/);
    words.innerHTML = text.map((w) => `<span class="w">${w}</span>`).join(' ');
    const spans = [...words.querySelectorAll('.w')];
    wordsUpdate = () => {
      const r = words.getBoundingClientRect();
      const vh = window.innerHeight;
      const p = clamp((vh * 0.88 - r.top) / (r.height + vh * 0.4), 0, 1);
      const n = Math.round(p * spans.length * 1.15);
      spans.forEach((s, i) => s.classList.toggle('on', i < n));
    };
  }

  /* ---------- parallax ---------- */
  const heroMedia = doc.querySelector('[data-hero-media]');
  const para = [...doc.querySelectorAll('[data-parallax]')];

  /* ---------- single rAF-throttled scroll loop ---------- */
  let ticking = false;
  const onScroll = () => {
    ticking = false;
    const y = window.scrollY;
    if (header) {
      header.classList.toggle('is-scrolled', y > 24);
      const menuOpen = root.classList.contains('cv-menu-open');
      header.classList.toggle('is-hidden', !menuOpen && y > lastY + 2 && y > window.innerHeight * 0.9);
      if (y < lastY - 2) header.classList.remove('is-hidden');
    }
    lastY = y;
    if (!reduce) {
      if (heroMedia && y < window.innerHeight * 1.2) heroMedia.style.transform = `translate3d(0, ${(y * 0.22).toFixed(1)}px, 0)`;
      para.forEach((el) => {
        const r = el.parentElement.getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight) return;
        const c = (r.top + r.height / 2 - window.innerHeight / 2) / window.innerHeight;
        el.style.setProperty('--py', (c * -60).toFixed(1) + 'px');
      });
    }
    if (hoursUpdate) hoursUpdate();
    if (wordsUpdate) wordsUpdate();
  };
  window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(onScroll); } }, { passive: true });
  window.addEventListener('resize', onScroll);
  onScroll();

  /* ---------- magnetic buttons (mouse only) ---------- */
  if (fine && !reduce) {
    doc.querySelectorAll('[data-magnetic]').forEach((btn) => {
      btn.addEventListener('pointermove', (e) => {
        const r = btn.getBoundingClientRect();
        btn.style.transform = `translate(${((e.clientX - r.left - r.width / 2) * 0.18).toFixed(1)}px, ${((e.clientY - r.top - r.height / 2) * 0.3).toFixed(1)}px)`;
      });
      btn.addEventListener('pointerleave', () => { btn.style.transform = ''; });
    });
  }

  /* ---------- newsletter: show Shopify's own confirmation / error state ---------- */
  const msg = doc.querySelector('[data-join-msg]');
  if (msg && /customer_posted=true/.test(location.search)) msg.hidden = false;
})();
